import type {APIGuestBookEntry, APIGuestBookSort} from '@app/common/api'
import {queryClient, useCurrentUser, useGuestBook, useMyGuestBookEntry} from '@app/common/api'
import {APIPermissions} from '@app/common/constants'
import {getAPIErrorMessage, getAPIValidationErrorMessage, hasFlag, isAdmin} from '@app/common/utils'
import styles from '@app/components/App.module.css'
import {Avatar} from '@app/components/Avatar/Avatar'
import {AlertDialog} from '@app/components/Modal/AlertDialog'
import {Spinner} from '@app/components/Spinner/Spinner'
import ErrorScreen from '@app/components/UI/ErrorScreen'
import {MetaTags} from '@app/components/UI/MetaTags'
import {Textarea} from '@app/components/UI/Textarea'
import {api} from '@app/hooks/useApi'
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  IconButton,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {FlagIcon, HandThumbDownIcon, HandThumbUpIcon, TrashIcon} from '@heroicons/react/24/solid'
import type React from 'react'
import {useEffect, useState} from 'react'
import toast from 'react-hot-toast'
import {Link as RouterLink} from 'react-router-dom'

const GUEST_BOOK_TEXT_MAX_LENGTH = 1024
const GUEST_BOOK_TEXT_MAX_NEWLINES = 3
const GUEST_BOOK_TEXT_CHARACTERS_ERROR = 'Only printable ASCII characters, excluding backticks, are supported.'
const GUEST_BOOK_TEXT_NEWLINES_ERROR = 'Guest book entries can use at most 3 line breaks.'
const GUEST_BOOK_TEXT_PATTERN = /^[\n -_a-z{-~]+$/

function normalizeGuestBookText(text: string): string {
  return text.trim().replace(/\r\n?/g, '\n')
}

function getGuestBookTextError(text: string): string {
  const normalized = normalizeGuestBookText(text)
  if (normalized.length === 0) return 'Guest book entries cannot be empty.'
  if (normalized.length > GUEST_BOOK_TEXT_MAX_LENGTH) return 'Guest book entries must be 1024 characters or fewer.'
  if (!GUEST_BOOK_TEXT_PATTERN.test(normalized)) return GUEST_BOOK_TEXT_CHARACTERS_ERROR
  if ((normalized.match(/\n/g) ?? []).length > GUEST_BOOK_TEXT_MAX_NEWLINES) return GUEST_BOOK_TEXT_NEWLINES_ERROR
  return ''
}

function updateGuestBookEntryCache(entry: APIGuestBookEntry, {updateMyEntry = false} = {}): void {
  queryClient.setQueriesData<APIGuestBookEntry[]>({queryKey: ['guest-book', 'list']}, entries => {
    if (!entries) return entries
    if (entries.some(current => current.xuid === entry.xuid)) {
      return entries.map(current => (current.xuid === entry.xuid ? entry : current))
    }

    return [entry, ...entries]
  })
  queryClient.setQueryData<APIGuestBookEntry | null>(['guest-book', 'me'], current => {
    if (updateMyEntry) return entry
    return current?.xuid === entry.xuid ? entry : current
  })
}

function removeGuestBookEntryCache(xuid: string): void {
  queryClient.setQueriesData<APIGuestBookEntry[]>({queryKey: ['guest-book', 'list']}, entries =>
    entries?.filter(current => current.xuid !== xuid),
  )
  queryClient.setQueryData<APIGuestBookEntry | null>(['guest-book', 'me'], entry =>
    entry?.xuid === xuid ? null : entry,
  )
}

function isValidGuestBookText(text: string): boolean {
  return getGuestBookTextError(text) === ''
}

function GuestBookEditor(): React.JSX.Element {
  const {data: user, isLoading: isLoadingUser} = useCurrentUser()
  const {data: entry, status} = useMyGuestBookEntry(Boolean(user))
  const [text, setText] = useState('')
  const [apiError, setApiError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (entry) {
      setText(entry.text)
    } else if (status === 'success') {
      setText('')
    }
  }, [entry, status])

  function updateText(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    setText(event.target.value)
    setApiError('')
  }

  async function save(): Promise<void> {
    const normalized = normalizeGuestBookText(text)
    const validationError = getGuestBookTextError(normalized)
    setApiError('')

    if (validationError) {
      toast.error(validationError)
      return
    }

    setIsSaving(true)
    try {
      const {data} = await api.put<APIGuestBookEntry>('guest-book/@me', {text: normalized})
      updateGuestBookEntryCache(data, {updateMyEntry: true})
      toast.success(entry ? 'Guest book entry updated.' : 'Guest book entry added.')
    } catch (error) {
      const message =
        getAPIValidationErrorMessage(error, 'text') ||
        getAPIErrorMessage(error, 'Failed to save your guest book entry.')
      setApiError(message)
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const validationError = text.length > 0 ? getGuestBookTextError(text) : ''

  if (!user && !isLoadingUser) {
    return (
      <Box borderWidth={1} className={styles.backdrop} p={5} rounded="2xl" textAlign="center">
        <Stack align="center" spacing={3}>
          <Heading fontSize="xl">Sign the Guest Book</Heading>
          <Text color="gray.200">Log in to leave or edit your message.</Text>
          <Button as={RouterLink} colorScheme="orange" to="/auth">
            Log In
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <Box borderWidth={1} className={styles.backdrop} p={5} rounded="2xl">
      <Stack spacing={3}>
        <Heading fontSize="xl">{entry ? 'Edit Your Entry' : 'Sign the Guest Book'}</Heading>
        <FormControl isInvalid={Boolean(apiError || validationError)}>
          <Textarea
            characterCount={text.length}
            isDisabled={!user || status === 'pending' || isSaving}
            maxLength={GUEST_BOOK_TEXT_MAX_LENGTH}
            minH="8rem"
            onChange={updateText}
            placeholder="Share your message..."
            value={text}
          />
          <FormErrorMessage whiteSpace="pre-wrap">{apiError || validationError}</FormErrorMessage>
        </FormControl>
        <Flex align="center" gap={3} justify="space-between">
          <Text color={isValidGuestBookText(text) ? 'gray.400' : 'red.300'} fontSize="sm">
            {text.length}/{GUEST_BOOK_TEXT_MAX_LENGTH}
          </Text>
          <Button colorScheme="orange" isDisabled={!isValidGuestBookText(text)} isLoading={isSaving} onClick={save}>
            {entry ? 'Save Changes' : 'Add Entry'}
          </Button>
        </Flex>
      </Stack>
    </Box>
  )
}

function GuestBookCard({entry, canModerate}: {entry: APIGuestBookEntry; canModerate: boolean}): React.JSX.Element {
  const {data: user} = useCurrentUser()
  const reportDialog = useDisclosure()
  const deleteDialog = useDisclosure()
  const [isVoting, setIsVoting] = useState(false)
  const [isReporting, setIsReporting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const isOwnEntry = user?.xuid === entry.xuid
  const reportCount = entry.reportCount ?? 0

  async function updateVote(vote: -1 | 1): Promise<void> {
    if (!user) {
      toast.error('Log in to vote on guest book entries.')
      return
    }

    setIsVoting(true)
    try {
      const nextVote = entry.viewerVote === vote ? 0 : vote
      const {data} = await api.put<APIGuestBookEntry>(`guest-book/${entry.xuid}/vote`, {vote: nextVote})
      updateGuestBookEntryCache(data)
    } catch (error) {
      toast.error(getAPIErrorMessage(error, 'Failed to update your vote.'))
    } finally {
      setIsVoting(false)
    }
  }

  async function report(): Promise<void> {
    if (!user) {
      toast.error('Log in to report guest book entries.')
      return
    }

    setIsReporting(true)
    try {
      const {data} = await api.put<APIGuestBookEntry>(`guest-book/${entry.xuid}/report`, {})
      updateGuestBookEntryCache(data)
      reportDialog.onClose()
      toast.success('Report received.')
    } catch (error) {
      toast.error(getAPIErrorMessage(error, 'Failed to report this entry.'))
    } finally {
      setIsReporting(false)
    }
  }

  async function deleteEntry(): Promise<void> {
    setIsDeleting(true)
    try {
      await api.delete(`guest-book/${entry.xuid}`)
      removeGuestBookEntryCache(entry.xuid)
      deleteDialog.onClose()
      toast.success('Guest book entry removed.')
    } catch (error) {
      toast.error(getAPIErrorMessage(error, 'Failed to remove this entry.'))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Box borderWidth={1} className={styles.backdrop} p={4} rounded="2xl">
      <Stack h="full" justify="space-between" spacing={4}>
        <Text fontSize="md" whiteSpace="pre-wrap" wordBreak="break-word">
          {entry.text}
        </Text>

        <Stack spacing={3}>
          <Flex align="center" gap={3}>
            <Avatar name={entry.player.name} shouldShowOnline={false} size={32} skinHash={entry.player.skinHash} />
            <Link as={RouterLink} fontWeight="bold" to={`/player/${entry.player.name}`}>
              {entry.player.name}
            </Link>
            <Badge bgColor="orange.500" color="white" ml="auto" rounded="md">
              {entry.score}
            </Badge>
            {canModerate && reportCount > 0 && (
              <Badge bgColor="red.500" color="white" rounded="md">
                {reportCount} reports
              </Badge>
            )}
          </Flex>

          <Flex gap={2}>
            <IconButton
              aria-label="Upvote"
              colorScheme={entry.viewerVote === 1 ? 'orange' : 'gray'}
              icon={<HandThumbUpIcon height={18} width={18} />}
              isDisabled={isVoting}
              onClick={() => updateVote(1)}
              size="sm"
              variant={entry.viewerVote === 1 ? 'solid' : 'ghost'}
            />
            <Text alignSelf="center" color="gray.300" fontSize="sm" minW="1.5rem">
              {entry.upvotes}
            </Text>

            <IconButton
              aria-label="Downvote"
              colorScheme={entry.viewerVote === -1 ? 'orange' : 'gray'}
              icon={<HandThumbDownIcon height={18} width={18} />}
              isDisabled={isVoting}
              onClick={() => updateVote(-1)}
              size="sm"
              variant={entry.viewerVote === -1 ? 'solid' : 'ghost'}
            />
            <Text alignSelf="center" color="gray.300" fontSize="sm" minW="1.5rem">
              {entry.downvotes}
            </Text>

            <IconButton
              aria-label="Report"
              colorScheme={entry.viewerReported ? 'red' : 'gray'}
              icon={<FlagIcon height={18} width={18} />}
              isDisabled={entry.viewerReported || isOwnEntry}
              ml="auto"
              onClick={reportDialog.onOpen}
              size="sm"
              variant={entry.viewerReported ? 'solid' : 'ghost'}
            />

            {canModerate && reportCount > 0 && (
              <IconButton
                aria-label="Remove"
                colorScheme="red"
                icon={<TrashIcon height={18} width={18} />}
                isDisabled={isDeleting}
                onClick={deleteDialog.onOpen}
                size="sm"
                variant="ghost"
              />
            )}
          </Flex>
        </Stack>
      </Stack>

      <AlertDialog
        content="This will send the entry to staff for review."
        isLoading={isReporting}
        isOpen={reportDialog.isOpen}
        onClose={reportDialog.onClose}
        onPrimary={report}
        primary="Report"
        title="Report Guest Book Entry"
      />
      <AlertDialog
        content="This will permanently remove the guest book entry."
        isLoading={isDeleting}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        onPrimary={deleteEntry}
        primary="Remove"
        title="Remove Guest Book Entry"
      />
    </Box>
  )
}

export default function GuestBook(): React.JSX.Element | null {
  const [sort, setSort] = useState<APIGuestBookSort>('score')
  const {data, error, status} = useGuestBook(sort)
  const {data: user} = useCurrentUser()
  const canModerate = Boolean(
    user && (isAdmin(user) || (user.staff && hasFlag(user.apiPermissions, APIPermissions.MANAGE_PLAYER_REPORTS))),
  )

  if (status === 'error') return <ErrorScreen payload={error} />

  return (
    <>
      <MetaTags
        description="Messages from the NetherGames community."
        title="Guest Book"
        url="https://portal.nethergames.org"
      />
      <Stack align="stretch" maxW="6xl" p={4} spacing={5} w="full">
        <Box borderWidth={1} className={styles.backdrop} p={5} rounded="2xl" textAlign="center">
          <Stack align="center" spacing={3}>
            <Heading fontSize={['2xl', '3xl']}>NetherGames Guest Book</Heading>
            <Text color="gray.200" maxW="3xl">
              Messages left by players before NetherGames closes on June 28, 2026.
            </Text>
            <Button
              as="a"
              colorScheme="orange"
              href="https://support.nethergames.org/closure-announcement"
              rel="noreferrer"
              target="_blank"
            >
              Closure Announcement
            </Button>
          </Stack>
        </Box>

        <GuestBookEditor />

        <Flex align={['stretch', 'center']} direction={['column', 'row']} gap={3} justify="space-between">
          <Text color="gray.300" fontSize="sm">
            {data ? `${data.length} entries` : 'Loading entries...'}
          </Text>
          <Select
            aria-label="Sort guest book entries"
            maxW={['full', '12rem']}
            onChange={event => setSort(event.target.value as APIGuestBookSort)}
            value={sort}
          >
            <option value="score">Top rated</option>
            <option value="newest">Newest</option>
          </Select>
        </Flex>

        {!data && (
          <Flex justify="center" py={12}>
            <Spinner />
          </Flex>
        )}

        {data?.length === 0 && (
          <Box borderWidth={1} className={styles.backdrop} p={5} rounded="2xl" textAlign="center">
            <Text>No guest book entries have been published yet.</Text>
          </Box>
        )}

        {data && data.length > 0 && (
          <SimpleGrid columns={[1, 1, 2]} gap={4}>
            {data.map(entry => (
              <GuestBookCard canModerate={canModerate} entry={entry} key={entry.xuid} />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </>
  )
}
